import {
  Button,
  ButtonVariant,
  CheckBox,
  CheckboxAlignment,
  Divider,
  IconIdentifier,
  Input,
  InputGroup,
  toast,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import { without } from 'lodash';
import { PolygonEventType } from 'map-3d/shared';
import {
  selectMap3DState,
  selectMap3dDataset,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleResponseMessage, useAnalyticsRequest } from 'shared/api';
import { GlobalContext } from 'shared/context';
import { FeatureFlag } from 'shared/enums';
import { MapTool } from '../../../map-3d-container/map-3d-tools';
import { setVectorLayerVisibility } from '../map-3d-workspace';
import { AnalyticsOutput } from './enums';

const ANALYTICS_OUTPUTS: AnalyticsOutput[] = [
  AnalyticsOutput.BenchAnalysis,
  AnalyticsOutput.HaulRoadGradient,
  AnalyticsOutput.HaulRoadWidth,
  AnalyticsOutput.DrainageAnalytics,
  AnalyticsOutput.SlopeStability,
  AnalyticsOutput.LandUseLandCover,
  AnalyticsOutput.SuperElevation,
];

export const GenerateRoadAnalytics: React.FC<{
  setShowAnalyticsForm: (show: boolean) => void;
}> = ({ setShowAnalyticsForm }) => {
  // State.
  const [inDrawingMode, setInDrawingMode] = useState<boolean>(false);
  const [hasPolygons, setHasPolygons] = useState<boolean>(false);
  const [selectedOutputs, setSelectedOutputs] = useState<string[]>([]);

  // Selectors.
  const { aoiManager } = useAppSelector(selectMap3DState);
  const { selectedTerrainIteration } = useAppSelector(selectMap3dDataset);
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const dispatch = useDispatch();

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  // Contexts.
  const { loggedUser } = useContext(GlobalContext);

  // Derive visible outputs by feature flags.
  const visibleAnalyticsOutputs = useMemo(() => {
    const flags = loggedUser?.featureFlags ?? {};
    const isLegacyGAEnabled = flags[FeatureFlag.GenerateAnalytics] === true;

    const outputToFlag: Record<AnalyticsOutput, FeatureFlag> = {
      [AnalyticsOutput.BenchAnalysis]:
        FeatureFlag.GenerateAnalyticsBenchAnalysisToeCrest,
      [AnalyticsOutput.HaulRoadGradient]:
        FeatureFlag.GenerateAnalyticsHaulRoadGradient,
      [AnalyticsOutput.HaulRoadWidth]:
        FeatureFlag.GenerateAnalyticsHaulRoadWidth,
      [AnalyticsOutput.DrainageAnalytics]:
        FeatureFlag.GenerateAnalyticsDrainageAnalytics,
      [AnalyticsOutput.SlopeStability]:
        FeatureFlag.GenerateAnalyticsSlopeStability,
      [AnalyticsOutput.LandUseLandCover]:
        FeatureFlag.GenerateAnalyticsLandUseLandCover,
      [AnalyticsOutput.SuperElevation]:
        FeatureFlag.GenerateAnalyticsSuperElevation,
    };

    // If legacy GA is enabled, show all options; else filter by individual flags.
    return isLegacyGAEnabled
      ? ANALYTICS_OUTPUTS
      : ANALYTICS_OUTPUTS.filter((out) => flags[outputToFlag[out]] === true);
  }, [loggedUser]);

  // Api.
  const {
    mutate: sendAnalyticsRequest,
    isPending: isAnalyticsRequestLoading,
    data: analyticsResponse,
    isSuccess: isAnalyticsRequestSuccess,
    isError: isAnalyticsRequestError,
    error: analyticsRequestError,
  } = useAnalyticsRequest();

  // useEffects.
  useEffect(() => {
    return () => {
      setVectorLayerVisibility(workspaceLayers, true);
    };
  }, []);

  useEffect(() => {
    if (!polygonTool || !inDrawingMode) {
      return;
    }

    const onPolygonCreated = () => {
      setHasPolygons(true);
    };

    polygonTool.addEventListener(
      PolygonEventType.PolygonCreated,
      onPolygonCreated,
    );

    return () => {
      polygonTool.removeEventListener(
        PolygonEventType.PolygonCreated,
        onPolygonCreated,
      );
    };
  }, [polygonTool, inDrawingMode]);

  useEffect(() => {
    handleResponseMessage(
      isAnalyticsRequestSuccess,
      isAnalyticsRequestError,
      analyticsResponse,
      analyticsRequestError,
    );
    if (isAnalyticsRequestSuccess) {
      setVectorLayerVisibility(workspaceLayers, true);
      setShowAnalyticsForm(false);
    }
  }, [analyticsResponse, isAnalyticsRequestSuccess, isAnalyticsRequestError]);

  // useMemo.
  const isParentCheckboxChecked = useMemo(() => {
    return selectedOutputs.length === visibleAnalyticsOutputs.length;
  }, [selectedOutputs, visibleAnalyticsOutputs]);

  const isParentCheckboxIndeterminate = useMemo(() => {
    return (
      selectedOutputs.length > 0 &&
      selectedOutputs.length < visibleAnalyticsOutputs.length
    );
  }, [selectedOutputs, visibleAnalyticsOutputs]);

  const isSubmitButtonDisabled =
    selectedOutputs.length < 1 || !hasPolygons || isAnalyticsRequestLoading;

  // Handlers.
  const onDrawBtnClick = () => {
    if (!inDrawingMode) {
      polygonTool?.activate({ isRoadAnalytics: true }, {}, true);
      dispatch(setCurrentActiveMapTool(MapTool.None));
      setVectorLayerVisibility(workspaceLayers, false);
    } else {
      polygonTool?.deactivate();
      setVectorLayerVisibility(workspaceLayers, true);
    }
    setInDrawingMode(!inDrawingMode);
  };

  const onClickParentCheckbox = () => {
    let updatedSelectedOutputs: string[] = [];
    if (!isParentCheckboxChecked && !isParentCheckboxIndeterminate) {
      updatedSelectedOutputs = [...visibleAnalyticsOutputs];
    }
    setSelectedOutputs(updatedSelectedOutputs);
  };

  const onClickCheckbox = (output: string) => {
    let updatedSelectedOutputs = [...selectedOutputs];
    if (updatedSelectedOutputs.includes(output)) {
      updatedSelectedOutputs = without(updatedSelectedOutputs, output);
    } else {
      updatedSelectedOutputs.push(output);
    }
    setSelectedOutputs([...updatedSelectedOutputs]);
  };

  const onSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const polygonWkt = polygonTool?.exportCurrentPolygonsToWkt();

    if (!polygonWkt?.[0]) {
      toast.error('Please draw a polygon first.');
      return;
    }

    if (selectedTerrainIteration && polygonWkt?.[0]) {
      sendAnalyticsRequest({
        polygonWkt: polygonWkt[0].wkt,
        selectedOutputs,
        iterationId: selectedTerrainIteration.id,
      });
    }
  };

  return (
    <form
      className="generate-road-analytics__form__container"
      onSubmit={onSubmit}
    >
      <div className="generate-road-analytics__form">
        <InputGroup className="generate-road-analytics__form__draw-section">
          <Input.Label>Mark Area</Input.Label>
          <Button
            onClick={onDrawBtnClick}
            leftIconIdentifier={IconIdentifier.Polygon}
            variant={
              inDrawingMode ? ButtonVariant.Primary : ButtonVariant.Outline
            }
            disabled={isAnalyticsRequestLoading}
          >
            {inDrawingMode ? 'Exit Drawing Mode' : 'Enter Drawing Mode'}
          </Button>
        </InputGroup>
        <Divider text={''} />
        <InputGroup className="generate-road-analytics__form__select-output">
          <div className="generate-road-analytics__form__select-output__parent-checkbox">
            <Input.Label>Select Outputs</Input.Label>
            <Input.CheckBox
              checked={isParentCheckboxChecked}
              indeterminate={isParentCheckboxIndeterminate}
              onClick={onClickParentCheckbox}
              disabled={isAnalyticsRequestLoading}
            />
          </div>
          {visibleAnalyticsOutputs.map((output, index) => (
            <CheckBox
              title={output}
              key={index}
              checked={selectedOutputs.includes(output)}
              alignCheckbox={CheckboxAlignment.Right}
              onClick={() => onClickCheckbox(output)}
              disabled={isAnalyticsRequestLoading}
              showCard={true}
            />
          ))}
        </InputGroup>
      </div>
      <div className="generate-road-analytics__form__submit-btn__container">
        <Button
          type="submit"
          className="generate-road-analytics__form__submit-btn"
          disabled={isSubmitButtonDisabled}
          isLoading={isAnalyticsRequestLoading}
        >
          Generate Analytics
        </Button>
      </div>
    </form>
  );
};
